import React, {useContext, useEffect, useMemo} from "react";

import {buyNowContext} from "../BuyNow";
import {useViewport} from "../../utils/custom_hooks";
import {DEVICE_TYPES, PLAN_NAMES, PRODUCT_CATEGORIES} from "../../../../constants";
import {calcPromoDiscount, calcPromoPercentage, calcTotalPrice} from "../../utils/pricingHelper";
import TotalPrice from "./TotalPrice";
import PromoAmount from "./PromoAmount";

const PriceView = ({hideDecimalIfWhole = true, includeShippingCost = false, className}) => {
  const {width} = useViewport();
  const {
    appInfo, lineItems, promoCode, salesTaxRates, shippingStageState: {state, country, shippingMethod}, planProducts
  } = useContext(buyNowContext);

  const businessName = appInfo.name;
  const promoDiscount = calcPromoDiscount(lineItems, promoCode, shippingMethod, country);
  const originalPrice = calcTotalPrice(lineItems, country, state, shippingMethod, salesTaxRates, null); // Purposefully passing in no promo code to get the original price.
  const promoPercentage = calcPromoPercentage(promoDiscount, originalPrice);
  
  const { threeMonthDiscount } = useMemo(() => {
    const threeMonthPlanLineItem = lineItems.find(li => li.product.name === PLAN_NAMES.MONTHLY_THREE_MONTHS_INITIAL);
    const monthlyPlan = planProducts.find(plan => plan.name === PLAN_NAMES.MONTHLY);

    // TODO: If we charge users annually, I need to update how we make this calculation.
    // For the current version of the purchase flow, we want to call out the 3 month discount the user gets before their membership billing kicks in.
    // This is only applicable for the Shapa flow, and when the user hasn't explicitly chosen another plan.
    return { threeMonthDiscount: threeMonthPlanLineItem && monthlyPlan ? Math.round(monthlyPlan.price * 3) : null };
  }, [lineItems, planProducts]);

  useEffect(() => {
    const cartButton = document.getElementById("cart-button");
    if (cartButton) {
      const containerWidth = cartButton.offsetLeft + cartButton.offsetWidth;
      document.getElementById("price-view-container").style.width = `${containerWidth}px`;
    }
  }, [width]);

  const prices = useMemo(() => {
    return lineItems.reduce((memo, lineItem) => {
      if (lineItem.product.category === PRODUCT_CATEGORIES.SCALE) {
        if (lineItem.product.deviceType === DEVICE_TYPES.SCALE_BP_BUNDLE)
          memo.deviceName = "bundle";
        else if (lineItem.product.deviceType === DEVICE_TYPES.BP_DEVICE) {
          if (lineItem.product.name.includes("Adolescent"))
            memo.deviceName = "Adolescent BP monitor";
          else
            memo.deviceName = "BP monitor";
        }
        else
          memo.deviceName = "scale";

        memo.devicePrice = lineItem.price;
        memo.total += lineItem.price;
      } else if (lineItem.product.category === PRODUCT_CATEGORIES.MEMBERSHIP) {
        memo.planPrice = threeMonthDiscount ?? lineItem.price;
        memo.total += threeMonthDiscount ?? lineItem.price;
      } else if (lineItem.product.category === PRODUCT_CATEGORIES.ADMIN_ACCESS) {
        memo.adminAccessPrice = lineItem.price;
        memo.total += lineItem.price;
      }
      return memo;
    }, {total: 0});
  }, [lineItems]);

  // Render nothing if there are no line items.
  if (!lineItems.length) {
    return null;
  }

  return (
    <section className={className}>
      <div id="price-view-container" className="d-flex flex-column">
        {(prices.devicePrice !== undefined) &&
          <div className="d-flex justify-content-between">
            <span style={{whiteSpace: 'nowrap'}}
                  className="fw-bolder">{businessName} {prices.deviceName}:</span>
            <span style={{whiteSpace: 'nowrap'}} className="fw-bolder">${prices.devicePrice}</span>
          </div>
        }
        {(prices.planPrice !== undefined) &&
          <div className="d-flex justify-content-between">
            <span style={{whiteSpace: 'nowrap'}} className="fw-bolder">{threeMonthDiscount ? '3-Month Membership' : 'Membership'}:</span>
            <span style={{whiteSpace: 'nowrap'}} className="fw-bolder">${prices.planPrice}</span>
          </div>
        }
        {prices.adminAccessPrice !== undefined &&
          <div className="d-flex justify-content-between">
            <span style={{whiteSpace: 'nowrap'}} className="fw-bolder">Admin access:</span>
            <span style={{whiteSpace: 'nowrap'}} className="fw-bolder">${prices.adminAccessPrice}</span>
          </div>
        }
        {(promoCode || threeMonthDiscount) &&
          <div>
            <div className="my-2" style={{borderBottom: "medium dashed black"}}/>
            <div className="d-flex justify-content-between" style={{textDecoration: 'line-through #7E7E7E'}}>
              <span style={{whiteSpace: 'nowrap'}}
                    className="fw-bolder grey-text">Total: ${prices.total.toFixed(2)}</span>
            </div>
            {threeMonthDiscount && <div className="d-flex justify-content-between">
              <span style={{whiteSpace: 'nowrap'}} className="fw-bolder red-text">
                <span style={{whiteSpace: 'nowrap'}} className="fw-bold">-${threeMonthDiscount}</span> (100% membership savings)
              </span>
            </div>}
            {promoCode && <div className="d-flex justify-content-between">
              <span style={{whiteSpace: 'nowrap'}} className="fw-bolder red-text">
                <span style={{whiteSpace: 'nowrap'}} className="fw-bold">-$<PromoAmount
                  hideDecimalIfWhole={hideDecimalIfWhole}/></span> ({promoPercentage}% savings)
              </span>
            </div>}
          </div>
        }
        <div className="my-2" style={{borderBottom: "medium dashed black"}}/>
        <div className="d-flex justify-content-between">
          <span style={{whiteSpace: 'nowrap'}} className="fw-bold">Total Today:</span>
          <span style={{whiteSpace: 'nowrap'}} className="fw-bold">$<TotalPrice hideDecimalIfWhole={hideDecimalIfWhole}
                                                                                includeShippingCost={includeShippingCost}/></span>
        </div>
      </div>
    </section>
  );
};

export default PriceView;
